import { Link } from "react-router";
import { useAuth } from "@/hooks/useAuth";
import {
  Users,
  Route,
  Zap,
  Building2,
  Gauge,
  Play,
  ShieldCheck,
  Lock,
  Clock,
  Award,
  Home,
  Droplets,
  GraduationCap,
  ShoppingCart,
  Sparkles,
  ArrowRight,
  ArrowUpRight,
} from "lucide-react";

const C = {
  lime: "#7CFF6B",
  limeDim: "rgba(124,255,107,0.15)",
  limeBorder: "rgba(124,255,107,0.30)",
  black: "#07110D",
  text: "#F7F8F4",
  muted: "#A8B8AE",
  cardBg: "rgba(7,17,13,0.75)",
  cardBorder: "rgba(247,248,244,0.08)",
};

export default function Hero() {
  const { isLoggedIn } = useAuth();
  return (
    <section className="relative min-h-[100dvh] overflow-hidden text-[#F7F8F4]" style={{ background: C.black }}>
      {/* BACKGROUND IMAGE - responsive with explicit dimensions */}
      <div className="absolute inset-0 z-0">
        <picture>
          <source
            srcSet="/assets/hero-flux/hero-1920.avif"
            type="image/avif"
            media="(min-width: 1200px)"
          />
          <source
            srcSet="/assets/hero-flux/hero-1600.avif"
            type="image/avif"
            media="(min-width: 800px)"
          />
          <source
            srcSet="/assets/hero-flux/hero-1280.avif"
            type="image/avif"
            media="(min-width: 600px)"
          />
          <source
            srcSet="/assets/hero-flux/hero-mobile.avif"
            type="image/avif"
          />
          <img
            src="/assets/hero-flux/hero-1280.jpg"
            alt="Aerial view of land parcel with development overlay"
            className="absolute inset-0 h-full w-full object-cover"
            loading="eager"
            width="1344"
            height="768"
            decoding="async"
            fetchPriority="high"
          />
        </picture>
        {/* Dark gradient from left for text readability */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(90deg, rgba(7,17,13,0.85) 0%, rgba(7,17,13,0.50) 40%, rgba(7,17,13,0.20) 60%, transparent 100%)",
          }}
        />
        {/* Bottom fade */}
        <div
          className="absolute inset-x-0 bottom-0 h-32"
          style={{ background: "linear-gradient(to top, rgba(7,17,13,1) 0%, rgba(7,17,13,0.3) 50%, transparent 100%)" }}
        />
      </div>

      {/* PROMINENT PRICING BANNER */}
      <div className="relative z-20 mx-auto max-w-[1440px] px-8 pt-4 lg:px-12">
        <Link
          to="/pricing"
          className="group flex items-center justify-center gap-2 rounded-xl border px-4 py-2.5 text-[12px] font-bold tracking-[0.08em] transition-all duration-300 hover:scale-[1.02]"
          style={{
            background: "linear-gradient(135deg, rgba(124,255,107,0.12) 0%, rgba(176,138,47,0.10) 50%, rgba(124,255,107,0.08) 100%)",
            borderColor: "rgba(124,255,107,0.35)",
            color: "#7CFF6B",
            boxShadow: "0 4px 20px rgba(124,255,107,0.10), inset 0 1px 0 rgba(124,255,107,0.15)",
          }}
        >
          <Sparkles size={13} className="transition-transform group-hover:rotate-12" />
          <span>START WITH 3 FREE SEARCHES — UPGRADE ANYTIME</span>
          <ArrowRight size={13} className="transition-transform group-hover:translate-x-1" />
        </Link>
      </div>

      {/* HERO CONTENT */}
      <div className="relative z-10 mx-auto flex min-h-[calc(100dvh-200px)] max-w-[1440px] flex-col items-start justify-center gap-8 px-8 lg:flex-row lg:items-center lg:justify-between lg:px-12">
        {/* LEFT COLUMN — Headline */}
        <div className="max-w-[540px]">
          <h1 className="text-[52px] font-black leading-[1.08] tracking-[-0.03em] md:text-[64px]">
            Find Tomorrow's
            <br />
            Best Land
            <br />
            Opportunities <span style={{ color: C.lime }}>Today.</span>
          </h1>

          <p className="mt-6 text-[17px] leading-[1.6] text-[#A8B8AE]">
            Data-powered insights. Real-time data.
            <br />
            Smarter land investments.
          </p>

          {/* CTAs */}
          <div className="mt-8 flex flex-wrap items-center gap-3">
            {isLoggedIn ? (
              <Link
                to="/pricing"
                className="flex items-center gap-2 rounded-md px-6 py-3 text-[14px] font-semibold tracking-[0.04em] transition-all hover:opacity-90"
                style={{ background: C.lime, color: C.black }}
              >
                <ArrowUpRight size={16} /> UPGRADE NOW
              </Link>
            ) : (
              <Link
                to="/register"
                className="rounded-md px-6 py-3 text-[14px] font-semibold tracking-[0.04em] transition-all hover:opacity-90"
                style={{ background: C.lime, color: C.black }}
              >
                START FREE TRIAL
              </Link>
            )}
            <Link
              to="/pricing"
              className="flex items-center gap-2 rounded-md border px-5 py-3 text-[13px] font-semibold tracking-[0.04em] transition-all hover:bg-[#F7F8F4]/5"
              style={{ borderColor: "rgba(124,255,107,0.40)", color: "#7CFF6B" }}
            >
              <Sparkles size={14} /> VIEW PRICING
            </Link>
            <Link
              to="/dashboard?demo=true"
              className="flex items-center gap-2 rounded-md border border-[#F7F8F4]/20 px-5 py-3 text-[13px] font-medium text-[#F7F8F4] transition-all hover:bg-[#F7F8F4]/5"
            >
              <Play size={14} fill="#F7F8F4" /> See How Scoring Works
            </Link>
          </div>

          {/* Trust Badges */}
          <div className="mt-8 flex flex-wrap items-center gap-5">
            <span className="flex items-center gap-1.5 text-[11px] font-medium tracking-[0.06em] text-[#A8B8AE]">
              <ShieldCheck size={14} style={{ color: C.lime }} /> REAL DATA
            </span>
            <span className="flex items-center gap-1.5 text-[11px] font-medium tracking-[0.06em] text-[#A8B8AE]">
              <Lock size={14} style={{ color: C.lime }} /> SECURE
            </span>
            <span className="flex items-center gap-1.5 text-[11px] font-medium tracking-[0.06em] text-[#A8B8AE]">
              <Clock size={14} style={{ color: C.lime }} /> UPDATED DAILY
            </span>
            <span className="flex items-center gap-1.5 text-[11px] font-medium tracking-[0.06em] text-[#A8B8AE]">
              <Award size={14} style={{ color: C.lime }} /> CERTIFIED DATA
            </span>
          </div>
        </div>

        {/* RIGHT COLUMN — Glass Stat Cards */}
        <div className="relative flex w-full max-w-[340px] flex-col gap-3 lg:w-auto">
          {/* Glass panel backdrop */}
          <div
            className="absolute -inset-4 rounded-2xl backdrop-blur-md"
            style={{
              background: "linear-gradient(135deg, rgba(247,248,244,0.08) 0%, rgba(247,248,244,0.02) 100%)",
              border: "1px solid rgba(247,248,244,0.12)",
              boxShadow: "0 8px 32px rgba(0,0,0,0.3), inset 0 1px 0 rgba(247,248,244,0.1)",
            }}
          />
          <div className="relative z-10 flex flex-col gap-3 p-2">
            <StatCard
              icon={<Users size={18} />}
              label="POPULATION GROWTH"
              value="+24.7%"
              sub="Strong Growth"
            />
            <StatCard
              icon={<Route size={18} />}
              label="HIGHWAY ACCESS"
              value="2.3 mi"
              sub="Excellent"
            />
            <StatCard
              icon={<Zap size={18} />}
              label="UTILITY ACCESS"
              value="All Available"
              sub="Excellent"
            />
            <StatCard
              icon={<Building2 size={18} />}
              label="NEW DEVELOPMENT"
              value="5 Projects"
              sub="Nearby"
            />
            <StatCard
              icon={<Gauge size={18} />}
              label="PLP OPPORTUNITY SCORE"
              value="92"
              sub="Heuristic Model (Demo)"
              highlight
            />
          </div>
        </div>
      </div>

      {/* BOTTOM INFRASTRUCTURE BAR */}
      <div className="relative z-10 mx-auto mt-auto max-w-[1440px] px-8 pb-6 lg:px-12">
        <div
          className="flex flex-wrap items-center justify-between gap-4 rounded-xl border px-6 py-4"
          style={{ background: C.cardBg, borderColor: C.cardBorder }}
        >
          <InfraStat icon={<Home size={16} />} label="NEW HOME CONSTRUCTION" value="328" sub="Units Planned" />
          <div className="h-8 w-px bg-[#F7F8F4]/10" />
          <InfraStat icon={<Route size={16} />} label="ROAD EXPANSION" value="0.5 mi" sub="In Progress" />
          <div className="h-8 w-px bg-[#F7F8F4]/10" />
          <InfraStat icon={<Droplets size={16} />} label="WATER LINE" value="0.4 mi" sub="Near Site" />
          <div className="h-8 w-px bg-[#F7F8F4]/10" />
          <InfraStat icon={<GraduationCap size={16} />} label="SCHOOL DEVELOPMENT" value="New ES" sub="Planned" />
          <div className="h-8 w-px bg-[#F7F8F4]/10" />
          <InfraStat icon={<ShoppingCart size={16} />} label="RETAIL GROWTH" value="4 Projects" sub="Within 3 Miles" />
        </div>
      </div>
    </section>
  );
}

/* ─── Glass Stat Card ─── */
function StatCard({
  icon,
  label,
  value,
  sub,
  highlight,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub: string;
  highlight?: boolean;
}) {
  return (
    <div
      className="group flex items-center gap-3 rounded-xl border px-4 py-3 transition-all duration-300"
      style={{
        background: highlight
          ? "linear-gradient(135deg, rgba(124,255,107,0.12) 0%, rgba(124,255,107,0.04) 100%)"
          : "linear-gradient(135deg, rgba(247,248,244,0.1) 0%, rgba(247,248,244,0.03) 100%)",
        borderColor: highlight ? C.limeBorder : "rgba(247,248,244,0.1)",
        boxShadow: highlight
          ? "0 4px 16px rgba(124,255,107,0.1), inset 0 1px 0 rgba(247,248,244,0.15)"
          : "0 4px 16px rgba(0,0,0,0.15), inset 0 1px 0 rgba(247,248,244,0.08)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
      }}
    >
      <div
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border transition-all duration-300 group-hover:scale-110"
        style={{
          color: C.lime,
          background: highlight ? "rgba(124,255,107,0.15)" : "rgba(247,248,244,0.08)",
          borderColor: highlight ? C.limeBorder : "rgba(247,248,244,0.1)",
        }}
      >
        {icon}
      </div>
      <div className="flex flex-col">
        <span className="text-[9px] font-semibold tracking-[0.12em] text-[#A8B8AE]">{label}</span>
        <span className="text-[16px] font-bold leading-tight text-[#F7F8F4]">{value}</span>
        <span className="text-[11px] font-medium" style={{ color: C.lime }}>{sub}</span>
      </div>
    </div>
  );
}

/* ─── Infrastructure Stat ─── */
function InfraStat({
  icon,
  label,
  value,
  sub,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub: string;
}) {
  return (
      <div className="flex items-center gap-2.5">
      <div className="flex h-8 w-8 items-center justify-center rounded-md" style={{ color: C.lime, background: C.limeDim }}>
        {icon}
      </div>
      <div className="flex flex-col">
        <span className="text-[8px] font-semibold tracking-[0.1em] text-[#A8B8AE]">{label}</span>
        <span className="text-[14px] font-bold leading-tight text-[#F7F8F4]">{value}</span>
        <span className="text-[10px] font-medium" style={{ color: C.lime }}>{sub}</span>
      </div>
    </div>
  );
}
